// RC8 · Ship Guide · "Decks" — a deck DIRECTORY (deck → venue listing)
// from the Widgety read model. Widgety supplies text only, so this is
// explicitly not a deck-plan drawing. The traveler's own deck is
// highlighted and scrolled into view when a cabin/deck is known.

import React, { useEffect, useMemo, useRef } from "react";
import { Layers, BedDouble, ChevronRight } from "lucide-react";
import { toPlainText, normalizeName, matchDiningVenue } from "./shipRefUtils";

export function deckNumber(name) {
  const m = String(name || "").match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

export function deckVenues(description) {
  const items = [...String(description || "").matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((m) => toPlainText(m[1]).trim())
    .filter(Boolean);
  if (items.length) return items;
  const plain = toPlainText(description).trim();
  return plain ? [plain] : [];
}

// Deck chip → catalogue record. Exact normalized name first (dining, then
// venues), then a conservative containment match (e.g. "Flowriders" ↔
// "FlowRider"). Unmatched chips stay plain text — never a dead link.
export function matchDeckVenue(label, dining, venues) {
  const norm = normalizeName(label);
  if (!norm) return null;
  const d = matchDiningVenue(label, dining);
  if (d) return { kind: "dining", item: d };
  const exact = (venues || []).find((v) => v && normalizeName(v.name) === norm);
  if (exact) return { kind: "venue", item: exact };
  if (norm.length < 5) return null;
  const contains = (a, b) => a.includes(b) || b.includes(a);
  const v = (venues || []).find((x) => x && contains(normalizeName(x.name), norm))
    || null;
  if (v) return { kind: "venue", item: v };
  const dn = (dining || []).find((x) => x && contains(normalizeName(x.name), norm)) || null;
  return dn ? { kind: "dining", item: dn } : null;
}

export function deckVenueDetail(match, deckName) {
  const it = match.item;
  return {
    type: match.kind,
    title: it.name,
    headline: deckName || null,
    aboutLabel: match.kind === "dining" ? "About this restaurant" : "About this venue",
    media: it.media,
    description: it.description,
    classification: it.experience || it.category || (it.categories && it.categories[0]) || null,
    menus: it.menus,
    matched: true,
  };
}

export function sortDecks(decks) {
  return [...(decks || [])].sort((a, b) => (deckNumber(b.name) ?? -1) - (deckNumber(a.name) ?? -1));
}

export function DecksTab({ decks, cabinDeck, cabinNumber, dining = [], venues = [], onOpenVenue }) {
  const ordered = useMemo(() => sortDecks(decks), [decks]);
  const mine = deckNumber(cabinDeck);
  const mineRef = useRef(null);
  useEffect(() => {
    if (mineRef.current) mineRef.current.scrollIntoView({ block: "center" });
  }, [ordered.length, mine]);

  if (!ordered.length) {
    return (
      <div className="py-10 text-center" data-testid="ship-guide-decks-empty">
        <p className="text-[14px] font-semibold text-[#475569]">Deck directory not available yet</p>
        <p className="mt-1 text-[12.5px] text-[#94A3B8]">Deck-by-deck venue listings appear here once the ship reference has loaded.</p>
      </div>
    );
  }
  return (
    <div data-testid="ship-guide-decks">
      <div className="flex items-start gap-2 text-[12.5px] text-[#64748B]" data-testid="ship-guide-decks-note">
        <Layers className="w-4 h-4 mt-0.5 shrink-0 text-[#B8894B]" aria-hidden="true" />
        <p>
          Deck directory — what is located on each deck, from the ship reference.
          This is a venue listing, not a deck-plan drawing. Outlined venues open their catalogue detail.
          {mine !== null && (
            <> Your stateroom{cabinNumber ? ` ${cabinNumber}` : ""} is on <span className="font-semibold text-[#0F2244]">Deck {mine}</span>.</>
          )}
        </p>
      </div>
      <ul className="mt-5 space-y-3">
        {ordered.map((d) => {
          const n = deckNumber(d.name);
          const isMine = mine !== null && n === mine;
          return (
            <li
              key={d.name}
              ref={isMine ? mineRef : null}
              className={`rounded-2xl border px-5 py-4 ${isMine ? "border-[#B8894B] bg-[#FBF7F0]" : "border-slate-200 bg-white"}`}
              data-testid={`ship-guide-deck-${n ?? "x"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-[17px] font-semibold text-[#0F2244]">{d.name}</h3>
                {isMine && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#0F2244] px-2.5 py-1 text-[11px] font-semibold text-white"
                        data-testid="ship-guide-deck-mine">
                    <BedDouble className="w-3 h-3" aria-hidden="true" /> Your deck{cabinNumber ? ` · Cabin ${cabinNumber}` : ""}
                  </span>
                )}
              </div>
              <ul className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {deckVenues(d.description).map((v, i) => {
                  const match = onOpenVenue ? matchDeckVenue(v, dining, venues) : null;
                  if (!match) {
                    return <li key={i} className="rounded-full bg-slate-100 px-2.5 py-1 text-[12px] text-[#334155]">{v}</li>;
                  }
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => onOpenVenue(deckVenueDetail(match, d.name))}
                        className="inline-flex items-center gap-1 rounded-full border border-[#B8894B]/40 bg-white px-2.5 py-1 text-[12px] font-medium text-[#0F2244] hover:bg-[#FBF7F0] hover:border-[#B8894B] transition-colors"
                        data-testid={`ship-guide-deck-venue-${n ?? "x"}-${i}`}
                      >
                        {v}
                        <ChevronRight className="w-3 h-3 text-[#B8894B]" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
